import './header.scss';

import React, {useCallback, useEffect} from 'react';
import {Navbar} from 'react-bootstrap';
import {useNavigate} from 'react-router-dom';
import {
    useIsAnalysisPage,
    useIsAssistantPage,
    useIsDocumentAssistantPage,
    useIsLandingPage,
    useIsSearchPage,
} from './header-hooks';
import {DefaultRootState, useDispatch, useSelector} from 'react-redux';
import {SearchBar} from '../search/search-bar/searchBar';
import {SaveSearchComponent} from '../search/save-search/saveSearchComponent';
import {exportRequestedAction, fetchFacets, search} from '../search/duck/search-actions';
import {
    useStateQuery,
    useStateQueryPending,
    useStateSavedSearches,
    useStateSavedSearchesSharedByOthers,
} from '../search/search-hooks';
import {SearchPageType} from '../search/search-config';
import {useTranslation} from 'react-i18next';
import {ReportDropdown} from '../search/export/exportDropdownComponent';
import {SearchExportFormat} from '../search/search-types';
import {doHTMLExport, doJsonExport} from '../services/search-service';
import {SavedSearchHistory} from '../search/save-search/history/savedSearchHistoryComponent';
import {SearchInfo} from '../search/search-results/search-info/search-info';
import {fetchSavedSearchesIfNeeded} from '../search/save-search/duck/saveSearchComponent-actions';
import {useStateContainerName} from '../settings/duck/settings-hooks';
import {SavedSearchWithId} from '../search/save-search/duck/saveSearchComponent-types';
import {Option} from '../common/select/select';
import {useSharedSavedSearchesEnabled} from '../config/config-hooks';
import {ConditionalRender} from '../common/conditional-render/ConditionalRender';
import {UserMenu} from './userMenu';
import {HeaderMenu} from './headerMenu';
import {clearAllSelectedFilters} from '../facets/duck/filter.actions';
import {useAreFiltersPersistent} from '../facets/duck/filter.hooks';

export const Header = (): React.JSX.Element => {
    const searchPageType = SearchPageType.BASIC;
    const dispatch = useDispatch();
    const query = useStateQuery();
    const config = useSelector((state: DefaultRootState) => state.config.uiConfig);
    const landingPageEnabled = config.landingPage.enabled;
    const isLandingPage = useIsLandingPage() && landingPageEnabled;
    const isSearchPage = useIsSearchPage();
    const isAnalysisPage = useIsAnalysisPage();
    const isAssistantPage = useIsAssistantPage();
    const isDocumentAssistantPage = useIsDocumentAssistantPage();
    const isPersistent: boolean = useAreFiltersPersistent();
    const navigate = useNavigate();
    const queryPending = useStateQueryPending();
    const {t} = useTranslation();

    const logoHibu = isLandingPage
        ? process.env.PUBLIC_URL + '/images/logo_start.svg'
        : process.env.PUBLIC_URL + '/images/logo_search.svg';
    const containerName = useStateContainerName();
    const savedSearches = useStateSavedSearches();
    const savedSearchesSharedByOthers = useStateSavedSearchesSharedByOthers();
    const savedSearchesSharedByOthersEnabled = useSharedSavedSearchesEnabled();

    useEffect(() => {
        dispatch(fetchSavedSearchesIfNeeded(searchPageType));
    }, [searchPageType, dispatch, containerName]);

    const triggerSearch = useCallback(
        (searchExpression?: string) => {
            if (!isPersistent) {
                dispatch(clearAllSelectedFilters());
            }
            dispatch(search(navigate, searchExpression));
            dispatch(fetchFacets(searchExpression));
        },
        [dispatch, isPersistent, navigate],
    );

    const onReportDropdownSelect = useCallback(
        (reportFormat: SearchExportFormat) => {
            dispatch(exportRequestedAction(searchPageType, reportFormat));

            if (reportFormat === SearchExportFormat.HTML) {
                dispatch(doHTMLExport(searchPageType));
            } else {
                dispatch(doJsonExport(searchPageType));
            }
        },
        [dispatch, searchPageType],
    );

    const navigateToHome = () => navigate('/');

    function mapToDistinctOptionsFromMultipleLists(...savedSearchLists: (SavedSearchWithId[] | undefined)[]): Option[] {
        const allSavedSearches = savedSearchLists
            .flat()
            .filter((search): search is SavedSearchWithId => search !== undefined);
        const uniqueFolders = new Set(allSavedSearches.map((search) => search.folder));
        return Array.from(uniqueFolders).map((folder) => ({
            value: folder,
            label: folder,
        }));
    }

    const folders = mapToDistinctOptionsFromMultipleLists(savedSearches, savedSearchesSharedByOthers);

    function renderHeader(): React.JSX.Element {
        return (
            <Navbar className={isLandingPage ? 'landing-page' : 'search-page'}>
                <Navbar.Brand>
                    <ConditionalRender shouldRender={config.logoUrl !== undefined}>
                        <a href={config.logoUrl}>
                            <img id={'logo'} src={logoHibu} alt={'Hibu'}/>
                        </a>
                    </ConditionalRender>
                    <ConditionalRender shouldRender={config.logoUrl === undefined}>
                        <img id={'logo'} src={logoHibu} alt={'Hibu'} onClick={navigateToHome}/>
                    </ConditionalRender>
                    <ConditionalRender shouldRender={isAssistantPage}>
                        <div className={'assistant-title'}>{t('assistant.title')}</div>
                    </ConditionalRender>
                    <ConditionalRender shouldRender={isDocumentAssistantPage}>
                        <div className={'assistant-title'}>{t('document-assistant.title')}</div>
                    </ConditionalRender>
                </Navbar.Brand>
                <div id="header-search" className={!isSearchPage ? 'header-search-proxy' : ''}>
                    <div id="search-bar-and-sources">
                        <SearchBar
                            placeholder={'search.label.placeholder'}
                            onSubmit={triggerSearch}
                            initialValue={String(query)}
                        />
                        <ConditionalRender shouldRender={!queryPending}>
                            <SearchInfo/>
                        </ConditionalRender>
                    </div>
                    <SaveSearchComponent
                        folderList={folders}
                        savedSearchesSharedByOthersEnabled={savedSearchesSharedByOthersEnabled}
                    />
                    <div id="search-buttons">
                        <SavedSearchHistory
                            savedSearches={savedSearches}
                            savedSearchesSharedByOthers={savedSearchesSharedByOthers}
                            savedSearchesSharedByOthersEnabled={savedSearchesSharedByOthersEnabled}
                        />
                        <ConditionalRender shouldRender={config.search.export && config.search.export.enabled}>
                            <ReportDropdown
                                onSelect={onReportDropdownSelect}
                                exportToggles={config.search.export.toggles}
                            />
                        </ConditionalRender>
                    </div>
                </div>
                <div id="header-right" className={isLandingPage ? 'landing-page' : 'search-page'}>
                    <UserMenu config={config} isLandingPage={isLandingPage}></UserMenu>
                    <HeaderMenu
                        config={config}
                        isSearchPage={isSearchPage}
                        isAnalysisPage={isAnalysisPage}
                        isAssistantPage={isAssistantPage}
                        isDocumentAssistantPage={isDocumentAssistantPage}
                    ></HeaderMenu>
                </div>
            </Navbar>
        );
    }

    return renderHeader();
};
